<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { RowSlot } from '@shared/ui/other'
import { ModalDefault } from '@shared/ui/modals'
import { AppTextarea, NativeSelect } from '@shared/ui/fields'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import DisciplineProgressReportSection from '../DisciplineProgressReportSection.vue'
import { useModalDisciplineProgressReport } from '../../model/modal-discipline-progress-report.hook'

defineOptions({
  name: 'ModalDisciplineProgressReport',
})

const REQUIRED_MESSAGE = 'Field is required'

const {
  refIsOpen,
  refIsViewOnly,
  refTitle,
  refIsSaving,
  refForm,
  refErrors,
  refOpenSection,
  refKidName,
  refKidGrade,
  refDisciplineName,
  refDateRange,
  refPositiveTraitOptions,
  sections,
  setPositiveTrait,
  setTopics,
  setStatement,
  toggleSection,
  closeModal,
  submit,
} = useModalDisciplineProgressReport()
</script>

<template>
  <ModalDefault
    :show="refIsOpen"
    :loading="refIsSaving"
    class-name="modal-discipline-progress-report-dialog"
    :title="refTitle"
    @click:overlay="closeModal"
  >
    <div class="modal-discipline-progress-report">
      <div class="modal-discipline-progress-report__info">
        <RowSlot label="Kid name:">
          <AppText name="body1-medium">
            {{ refKidName }}
          </AppText>
        </RowSlot>
        <RowSlot label="Kid's Grade:">
          <AppText name="body1-medium">
            {{ refKidGrade }}
          </AppText>
        </RowSlot>
        <RowSlot label="Discipline:">
          <AppText name="body1-medium">
            {{ refDisciplineName }}
          </AppText>
        </RowSlot>
        <RowSlot label="Date range:">
          <AppText name="body1-medium">
            {{ refDateRange }}
          </AppText>
        </RowSlot>
      </div>
      <div class="modal-discipline-progress-report__main">
        <div class="modal-discipline-progress-report__field">
          <NativeSelect
            name="positive_trait"
            label="Positive learning trait"
            placeholder="Not selected"
            placeholder-enabled
            required
            :value="refForm.positiveTrait || null"
            :options="refPositiveTraitOptions"
            :disabled="refIsSaving || refIsViewOnly"
            :mode="refErrors.positiveTrait ? 'error' : 'normal'"
            @change="setPositiveTrait"
          />
          <AppText
            v-if="refErrors.positiveTrait"
            name="body2"
            color="red-500"
          >
            {{ REQUIRED_MESSAGE }}
          </AppText>
        </div>
        <AppTextarea
          label="Topics covered"
          placeholder="Type here"
          required
          :value="refForm.topics"
          :disabled="refIsSaving || refIsViewOnly"
          :mode="refErrors.topics ? 'error' : 'normal'"
          :error-text="REQUIRED_MESSAGE"
          @input="setTopics"
        />
        <DisciplineProgressReportSection
          v-for="section in sections"
          :key="section.group"
          :title="section.title"
          :open="refOpenSection === section.group"
          :statements="refForm[section.group]"
          :errors="refErrors[section.group]"
          :disabled="refIsSaving || refIsViewOnly"
          @click:toggle="toggleSection(section.group)"
          @update:statement="(key, value) => setStatement(section.group, key, value)"
        />
      </div>
    </div>
    <template #controls>
      <div class="modal-discipline-progress-report__controls">
        <BtnPrimary
          v-if="!refIsViewOnly"
          text="Save"
          color="emerald"
          :disabled="refIsSaving"
          @click="submit"
        />
        <BtnSecondary
          text="Close"
          color="black"
          :disabled="refIsSaving"
          @click="closeModal"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalDisciplineProgressReport.scss" />
